// Match a snippet of text to the block(s) of a larger text that most likely
// contain it, by CONTENT-word overlap rather than a contiguous substring
// match. Extracted from the OCR-citation matcher (MDRS-134,
// `components/FilePreview/utils/matchSnippetToLines.ts`) so a second caller
// (the Scribe-to-Report-Builder back-link, SCRIBE-07) doesn't fork the same
// scoring logic, stop-word list, and thresholds into a second copy that can
// silently drift from this one.
//
// A "block" is whatever granularity the caller cares about — an OCR line, a
// transcript segment, a sentence. The AI/paraphrased text that names a
// passage rarely shares a long CONTIGUOUS run of words with the source (dates
// reflow, wording is reworded, transcripts get lightly edited) — an earlier
// longest-common-run approach either missed the real passage or latched onto
// a clean run of boilerplate. Bag-of-words overlap tolerates paraphrase and
// reordering: blocks with a strong overlap match; blocks that share nothing
// meaningful score zero and are left alone. When nothing clears the bar the
// caller should fall back to a coarser jump (e.g. "just land on the page/tab"
// with no highlight) rather than guess.

// A block is matched if it shares at least this many distinct content words
// with the snippet AND those shared words cover at least
// MIN_MULTI_HIT_COVERAGE of the block's own content words...
const MIN_SHARED_CONTENT_WORDS = 2;
// ...the coverage floor for the multi-hit case. Without it a long block that
// happens to share two incidental words with the snippet lights up even
// though it isn't the cited passage. Requiring the shared words to cover a
// third of the block's own words keeps genuine multi-block passages while
// dropping incidental long-block overlaps.
const MIN_MULTI_HIT_COVERAGE = 0.34;
// ...OR it shares at least one AND that covers this fraction of its own
// content words (so a short, fully-matched block still lights up without
// needing two hits).
const MIN_BLOCK_COVERAGE = 0.5;

// High-frequency words that carry no locating signal, plus common
// medical-form/transcript scaffolding. A shared word from this set is not
// evidence a block is the cited passage.
const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "been",
  "but",
  "by",
  "can",
  "for",
  "from",
  "had",
  "has",
  "have",
  "he",
  "her",
  "him",
  "his",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "must",
  "of",
  "on",
  "or",
  "provided",
  "she",
  "that",
  "the",
  "their",
  "this",
  "to",
  "use",
  "used",
  "was",
  "were",
  "which",
  "who",
  "will",
  "with",
  "you",
  "your",
]);

const tokenize = (value: string): string[] =>
  value
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);

const contentWords = (value: string): string[] =>
  tokenize(value).filter((w) => !STOP_WORDS.has(w));

/**
 * Find the indices of the block(s) a snippet touches, by distinct
 * content-word overlap. Returns the sorted indices of every block that
 * clears the bar, or `null` when nothing does (empty snippet, no blocks, or
 * only incidental/stop-word overlap).
 */
export const matchSnippetToBlocks = (
  blocks: string[],
  snippet: string
): number[] | null => {
  const needle = new Set(contentWords(snippet));
  if (needle.size === 0) return null;
  if (blocks.length === 0) return null;

  const matched: number[] = [];

  blocks.forEach((text, blockIndex) => {
    const blockWords = contentWords(text);
    if (blockWords.length === 0) return;

    const distinct = new Set(blockWords);
    let shared = 0;
    for (const word of distinct) {
      if (needle.has(word)) shared++;
    }
    if (shared === 0) return;

    const coverage = shared / distinct.size;
    const multiHit =
      shared >= MIN_SHARED_CONTENT_WORDS && coverage >= MIN_MULTI_HIT_COVERAGE;
    if (multiHit || coverage >= MIN_BLOCK_COVERAGE) {
      matched.push(blockIndex);
    }
  });

  return matched.length > 0 ? matched : null;
};
